import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowRight, Heart, Lightbulb, Palette, Code, Rocket, Star } from 'lucide-react';

const DevelopmentProcessSection = () => {
  const journeySteps = [
    {
      icon: <Heart className="h-8 w-8 text-red-500" />,
      step: "1",
      title: "Deine Vision verstehen",
      subtitle: "Der Moment, wo alles beginnt",
      story: "Wir setzen uns zusammen und träumen. Was soll deine App bewirken? Welche Probleme löst sie? Welche Emotionen soll sie auslösen? Hier entsteht die Magie."
    },
    {
      icon: <Lightbulb className="h-8 w-8 text-yellow-500" />,
      step: "2", 
      title: "Die perfekte Strategie",
      subtitle: "Vom Traum zum Plan",
      story: "Jetzt wird aus deiner Vision ein konkreter Plan. Wir definieren Features, die wirklich wichtig sind, und bauen eine Roadmap, die zum Erfolg führt."
    },
    {
      icon: <Palette className="h-8 w-8 text-purple-500" />,
      step: "3",
      title: "Design, das verzaubert",
      subtitle: "Wo Schönheit auf Funktion trifft",
      story: "Hier entstehen die ersten visuellen Eindrücke deiner App. Jeder Pixel wird mit Bedacht gesetzt, jede Animation hat einen Zweck. Das ist Design mit Seele."
    },
    {
      icon: <Code className="h-8 w-8 text-blue-500" />,
      step: "4",
      title: "Entwicklung mit Leidenschaft",
      subtitle: "Code wird zu Leben erweckt",
      story: "Jetzt wird programmiert - aber nicht einfach nur Code geschrieben. Jede Funktion wird mit Liebe zum Detail entwickelt. Du siehst täglich, wie deine App wächst."
    },
    {
      icon: <Star className="h-8 w-8 text-green-500" />,
      step: "5",
      title: "Perfektion durch Tests",
      subtitle: "Jedes Detail muss stimmen",
      story: "Wir testen nicht nur - wir perfektionieren. Auf jedem Gerät, in jeder Situation. Deine App soll nicht nur funktionieren, sie soll brillieren."
    },
    {
      icon: <Rocket className="h-8 w-8 text-orange-500" />,
      step: "6",
      title: "Der große Launch",
      subtitle: "Deine App erobert die Welt",
      story: "Der Moment der Wahrheit: Deine App geht live. Wir begleiten jeden Schritt des Launches und feiern gemeinsam mit dir diesen besonderen Moment."
    }
  ];

  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="container">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Die Reise zu <span className="text-blue-600">deiner App</span>
          </h2>
          <p className="text-lg text-gray-600">
            Jede großartige App beginnt mit einem Traum. Lass uns gemeinsam diese 
            Reise antreten und deine Vision Realität werden lassen.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {journeySteps.map((step, index) => (
            <Card key={index} className="border-0 shadow-lg bg-white h-full hover:shadow-xl transition-all duration-300 group">
              <CardContent className="p-8 h-full flex flex-col">
                <div className="flex items-center mb-6">
                  <div className="bg-gradient-to-r from-blue-100 to-purple-100 rounded-full w-10 h-10 flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300">
                    <span className="text-sm font-bold text-blue-600">{step.step}</span>
                  </div>
                  <div className="group-hover:scale-110 transition-transform duration-300">
                    {step.icon}
                  </div>
                </div>
                
                <h3 className="text-xl font-bold mb-2">{step.title}</h3>
                <p className="text-blue-600 font-semibold mb-4">{step.subtitle}</p>
                
                <p className="text-gray-700 leading-relaxed italic">
                  "{step.story}"
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Journey Promise */}
        <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl shadow-xl p-8 max-w-4xl mx-auto text-center">
          <div className="mb-6">
            <h3 className="text-2xl font-bold mb-4 text-gray-900">
              Unser Versprechen an dich
            </h3>
            <p className="text-lg text-gray-600 mb-8">
              Diese Reise gehst du nicht allein. Wir sind bei jedem Schritt an deiner Seite - 
              mit Transparenz, Leidenschaft und dem unbedingten Willen, deine App zum Erfolg zu führen.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <Heart className="h-8 w-8 text-red-500 mx-auto mb-3" />
              <h4 className="font-bold text-gray-900 mb-2">Mit Herz entwickelt</h4>
              <p className="text-gray-700 text-sm">
                Jede App ist für uns ein persönliches Projekt
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <Lightbulb className="h-8 w-8 text-yellow-500 mx-auto mb-3" />
              <h4 className="font-bold text-gray-900 mb-2">Volle Transparenz</h4>
              <p className="text-gray-700 text-sm">
                Du weißt immer, wo wir stehen und was als nächstes kommt
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <Rocket className="h-8 w-8 text-blue-500 mx-auto mb-3" />
              <h4 className="font-bold text-gray-900 mb-2">Langfristig begleitet</h4>
              <p className="text-gray-700 text-sm">
                Auch nach dem Launch sind wir für dich da
              </p>
            </div>
          </div>
          
          <a 
            href="#contact" 
            className="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
          >
            Meine App-Reise starten
            <ArrowRight className="ml-2 h-4 w-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default DevelopmentProcessSection; 